import { useCallback, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTran } from "@/hooks";
import { Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

const SIZE_MAP = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  "2xl": "sm:max-w-5xl",
  "3xl": "sm:max-w-6xl",
};

const AppDialog = ({
  open,
  onOpenChange,
  title,
  description,
  icon: Icon,
  header,
  size = "md",
  children,
  footer,
  contentClassName = "",
  headerWrapperClassName = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  headerExtra,
  closeButtonClassName = "",
  hideCloseButton = false,
  lockClose = false,
  isDirty = false,
}) => {
  const { t } = useTran();
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen) => {
      if (!nextOpen && isDirty) {
        setShowUnsavedDialog(true);
        return;
      }
      onOpenChange?.(nextOpen);
    },
    [isDirty, onOpenChange],
  );

  const handleConfirmDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleCancelDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  const handleEscapeKeyDown = (event) => {
    if (lockClose) {
      event.preventDefault();
    }
    if (isDirty) {
      event.preventDefault();
      setShowUnsavedDialog(true);
    }
  };

  const headerContent =
    header !== undefined ? (
      header
    ) : title ? (
      <div className="flex min-w-0 items-start gap-3">
        {Icon ? (
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
        ) : null}
        <div className="min-w-0 space-y-1">
          <DialogTitle className="text-xl font-semibold leading-tight text-foreground text-primary">
            {title}
          </DialogTitle>
          {description ? (
            <DialogDescription className="text-sm leading-snug text-muted-foreground">
              {description}
            </DialogDescription>
          ) : null}
        </div>
      </div>
    ) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          onEscapeKeyDown={handleEscapeKeyDown}
          className={cn(
            "flex w-[calc(100vw-1rem)] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] flex-col overflow-hidden border border-border bg-card p-5 sm:w-full sm:p-4 sm:rounded-radius",
            sizeClass,
            contentClassName,
          )}
        >
          <div
            className={cn(
              "flex items-start justify-between gap-3 border-b border-border pb-3 sm:gap-4",
              headerWrapperClassName,
            )}
          >
            <DialogHeader
              className={cn("min-w-0 flex-1 gap-2 text-left", headerClassName)}
            >
              {headerContent}
            </DialogHeader>
            {!hideCloseButton ? (
              <DialogClose
                className={cn(
                  "inline-flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground",
                  closeButtonClassName,
                )}
              >
                <X className="h-5 w-5" strokeWidth={2} />
                <span className="sr-only">{t("Close")}</span>
              </DialogClose>
            ) : null}
          </div>
          {headerExtra ? <div className="w-full">{headerExtra}</div> : null}

          {children ? (
            <div
              className={cn(
                "min-h-0 flex-1 flex flex-col overflow-y-auto !px-0 !py-3 sm:!py-4",
                bodyClassName,
              )}
            >
              {children}
            </div>
          ) : null}

          {footer ? (
            <div>
              <div className="mb-3 h-px bg-border sm:mb-4" />
              <DialogFooter className={cn("", footerClassName)}>
                {footer}
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={showUnsavedDialog}
        onOpenChange={(v) => !v && handleCancelDiscard()}
      >
        <DialogContent
          showCloseButton={false}
          className="flex w-[calc(100vw-1rem)] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] flex-col overflow-hidden border border-border bg-card p-5 sm:w-full sm:p-4 sm:rounded-radius sm:max-w-md"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border pb-3 sm:gap-4">
            <DialogHeader className="min-w-0 flex-1 gap-2 text-left">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Info className="h-5 w-5" />
                </span>
                <div className="min-w-0 space-y-1">
                  <DialogTitle className="text-xl font-semibold leading-tight text-destructive">
                    {t("Unsaved Changes")}
                  </DialogTitle>
                  <DialogDescription className="text-sm leading-snug text-muted-foreground">
                    {t("Leaving now will discard your latest edits.")}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>
          <div className="min-h-0 flex-1 flex flex-col overflow-y-auto !px-0 !py-3 sm:!py-4">
            <p className="text-sm text-muted-foreground">
              {t(
                "You have unsaved changes. Are you sure you want to leave without saving? Any unsaved changes will be lost.",
              )}
            </p>
          </div>
          <div>
            <div className="mb-3 h-px bg-border sm:mb-4" />
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelDiscard}>
                {t("Cancel")}
              </Button>
              <Button variant="destructive" onClick={handleConfirmDiscard}>
                {t("Discard Changes")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppDialog;
